<template>
  <component
    :is="as"
    :type="as === 'button' ? 'button' : undefined"
    class="heart-like-btn"
    :class="{ 'is-liked': modelValue }"
    :aria-pressed="as === 'button' ? modelValue : undefined"
    :aria-label="
      as === 'button'
        ? (ariaLabel || (modelValue ? 'Remove from selection' : 'Save to selection'))
        : undefined
    "
    @click="as === 'button' ? handleClick($event) : undefined"
  >
    <!-- Expanding burst ring -->
    <span
      v-if="animState === 'like'"
      class="burst-ring"
      aria-hidden="true"
    />

    <!-- Radial particle sparks -->
    <span
      v-if="animState === 'like'"
      class="burst-sparks"
      aria-hidden="true"
    >
      <span
        v-for="i in 7"
        :key="i"
        class="spark"
        :style="{ '--spark-idx': i }"
      />
    </span>

    <!-- Heart SVG Wrapper -->
    <span
      class="heart-like-wrapper"
      :class="{
        'animate-like': animState === 'like',
        'animate-unlike': animState === 'unlike'
      }"
      aria-hidden="true"
    >
      <svg
        class="heart-svg"
        :width="size"
        :height="size"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          class="heart-path"
          d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"
          :fill="modelValue ? activeColor : 'none'"
          :stroke="modelValue ? activeColor : inactiveColor"
          :stroke-width="strokeWidth"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    as?: string
    modelValue?: boolean
    size?: number | string
    strokeWidth?: number | string
    ariaLabel?: string
    activeColor?: string
    inactiveColor?: string
  }>(),
  {
    as: 'button',
    modelValue: false,
    size: 22,
    strokeWidth: 2,
    ariaLabel: '',
    activeColor: '#d97757',
    inactiveColor: '#5f5e5a'
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'click', event: MouseEvent): void
  (e: 'toggle', value: boolean): void
}>()

const animState = ref<'none' | 'like' | 'unlike'>('none')
let animTimeout: number | undefined
let modalCloseTimeout: number | undefined

const appState = useAppState()
const pendingAnimation = ref<'like' | 'unlike' | null>(null)

const computedSizePx = computed(() => {
  return typeof props.size === 'number' ? `${props.size}px` : props.size
})

function triggerAnimation(state: 'like' | 'unlike') {
  if (animTimeout) {
    window.clearTimeout(animTimeout)
  }

  animState.value = state

  animTimeout = window.setTimeout(() => {
    animState.value = 'none'
  }, state === 'like' ? 700 : 350)
}

watch(
  () => props.modelValue,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      if (newValue && appState?.isSaveModalOpen?.value) {
        // Defer like/save animation until the save modal closes so the user can see it
        pendingAnimation.value = 'like'
      } else {
        pendingAnimation.value = null
        triggerAnimation(newValue ? 'like' : 'unlike')
      }
    }
  }
)

watch(
  () => appState?.isSaveModalOpen?.value,
  (isOpen) => {
    if (!isOpen && pendingAnimation.value) {
      const stateToAnimate = pendingAnimation.value
      pendingAnimation.value = null
      if (modalCloseTimeout) {
        window.clearTimeout(modalCloseTimeout)
      }
      // Wait slightly for modal backdrop exit transition to clear
      modalCloseTimeout = window.setTimeout(() => {
        triggerAnimation(stateToAnimate)
      }, 200)
    }
  }
)

function handleClick(event: MouseEvent) {
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation?.()
  const nextValue = !props.modelValue

  emit('click', event)
  emit('update:modelValue', nextValue)
  emit('toggle', nextValue)
}

onBeforeUnmount(() => {
  if (animTimeout) {
    window.clearTimeout(animTimeout)
  }
  if (modalCloseTimeout) {
    window.clearTimeout(modalCloseTimeout)
  }
})
</script>

<style scoped>
.heart-like-btn {
  --heart-color: v-bind(inactiveColor);
  --heart-active-color: v-bind(activeColor);
  --btn-size: v-bind(computedSizePx);

  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: var(--btn-size, 32px);
  height: var(--btn-size, 32px);

  padding: 0;
  margin: 0;

  border: 0;
  background: transparent;

  color: var(--heart-color);
  cursor: pointer;

  -webkit-tap-highlight-color: transparent;
  user-select: none;
  flex-shrink: 0;
  overflow: visible;
}

.heart-like-btn.is-liked {
  --heart-color: var(--heart-active-color);
}

.heart-like-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  transform-origin: center;
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.heart-svg {
  display: block;
}

.heart-path {
  transition:
    fill 200ms ease,
    stroke 200ms ease;
}

/* Hover on unselected state */
.heart-like-btn:not(.is-liked):hover .heart-path {
  stroke: #0a0a0a;
}

/* Keyboard accessibility */
.heart-like-btn:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 3px;
  border-radius: 50%;
}

/* Press feedback */
.heart-like-btn:active .heart-like-wrapper {
  transform: scale(0.8);
}

/* Burst shockwave ring */
.burst-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  width: var(--btn-size);
  height: var(--btn-size);
  margin-top: calc(var(--btn-size) / -2);
  margin-left: calc(var(--btn-size) / -2);
  border-radius: 50%;
  border: 2px solid var(--heart-active-color);
  pointer-events: none;
  z-index: 1;
  animation: insta-ring-burst 600ms cubic-bezier(0.1, 0.8, 0.3, 1) forwards;
}

@keyframes insta-ring-burst {
  0% {
    transform: scale(0.2);
    border-width: 3px;
    opacity: 0.9;
  }
  50% {
    border-width: 2px;
    opacity: 0.6;
  }
  100% {
    transform: scale(2.2);
    border-width: 0;
    opacity: 0;
  }
}

/* Radial burst sparks */
.burst-sparks {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  pointer-events: none;
  z-index: 1;
}

.spark {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 4px;
  height: 4px;
  margin-top: -2px;
  margin-left: -2px;
  border-radius: 50%;
  background-color: var(--heart-active-color);
  pointer-events: none;
  animation: insta-spark-fly 650ms cubic-bezier(0.12, 0.8, 0.32, 1) forwards;
}

.spark::after {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background-color: #f59e0b;
}

@keyframes insta-spark-fly {
  0% {
    transform: rotate(calc(var(--spark-idx) * (360deg / 7))) translateY(0) scale(0.6);
    opacity: 1;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: rotate(calc(var(--spark-idx) * (360deg / 7))) translateY(calc(var(--btn-size) * -1.2)) scale(0);
    opacity: 0;
  }
}

/* Instagram-like pop animation */
.heart-like-wrapper.animate-like {
  animation:
    insta-like-pop
    600ms
    cubic-bezier(0.175, 0.885, 0.32, 1.4)
    forwards;
}

@keyframes insta-like-pop {
  0% {
    transform: scale(0.2);
  }
  30% {
    transform: scale(1.45);
  }
  55% {
    transform: scale(0.85);
  }
  75% {
    transform: scale(1.15);
  }
  90% {
    transform: scale(0.96);
  }
  100% {
    transform: scale(1);
  }
}

/* Unlike animation */
.heart-like-wrapper.animate-unlike {
  animation:
    insta-unlike-pop
    350ms
    cubic-bezier(0.25, 0.46, 0.45, 0.94)
    forwards;
}

@keyframes insta-unlike-pop {
  0% {
    transform: scale(1);
  }
  35% {
    transform: scale(0.72);
  }
  70% {
    transform: scale(1.08);
  }
  100% {
    transform: scale(1);
  }
}

/* Respect reduced motion */
@media (prefers-reduced-motion: reduce) {
  .heart-like-wrapper,
  .heart-path {
    transition: none;
  }

  .burst-ring,
  .burst-sparks,
  .spark,
  .heart-like-wrapper.animate-like,
  .heart-like-wrapper.animate-unlike {
    animation: none !important;
    display: none !important;
  }
}
</style>

